import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { map, Observable, tap } from 'rxjs';
import {
  LoginRequest,
  LoginResponse,
  MessageResponse,
  RequestPasswordReset,
  ResetPasswordRequest,
} from '../../shared/model/auth.interface';
import { API_CONFIG } from '../api/api.config';
import { TokenStorageService } from '../auth/token-storage.service';
import { Router } from '@angular/router';

/**
 * Servicio centralizado para la gestión de la autenticación y el ciclo de vida de la sesión.
 * * Se encarga de la comunicación con los endpoints de identidad del backend, gestionando
 * el inicio de sesión, la recuperación de credenciales y la renovación automática de tokens.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** Cliente HTTP para comunicaciones externas. */
  private readonly http = inject(HttpClient);
  /** URL base de la API definida en la configuración global. */
  private readonly baseUrl = API_CONFIG.baseURL;
  /** Servicio para persistir y recuperar tokens del almacenamiento local. */
  private readonly tokenStorage = inject(TokenStorageService);
  /** Servicio de navegación para redirecciones tras eventos de autenticación. */
  private readonly router = inject(Router);

  /**
   * Realiza la petición de inicio de sesión al servidor.
   * * Envía las credenciales y activa `withCredentials` para permitir el manejo de
   * cookies seguras (como el Refresh Token) por parte del navegador si el backend lo requiere.
   * * @param credentials Objeto con el email y password del usuario.
   * @returns Un `Observable` que emite un `LoginResponse` con los datos del perfil y el Access Token.
   * * @example
   * this.authService.login(myCredentials).subscribe(response => { ... });
   */
  login(credentials: LoginRequest): Observable<LoginResponse> {
    return this.http.post<LoginResponse>(`${this.baseUrl}/users/login`, credentials, {
      withCredentials: true,
    });
  }

  /**
   * Solicita un enlace de restablecimiento de contraseña al correo electrónico proporcionado.
   * * @param credentials Objeto que contiene el email del usuario solicitante.
   * @returns Un `Observable` que emite un `MessageResponse` con la confirmación del envío.
   */
  requestPasswordReset(credentials: RequestPasswordReset): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(
      `${this.baseUrl}/users/request-password-reset`,
      credentials,
    );
  }

  /**
   * Actualiza la contraseña del usuario utilizando un token de validación previo.
   * * @param credentials Objeto con la nueva contraseña y el token de restablecimiento.
   * @returns Un `Observable` que emite un `MessageResponse` indicando el éxito de la operación.
   */
  resetPassword(credentials: ResetPasswordRequest): Observable<MessageResponse> {
    return this.http.post<MessageResponse>(`${this.baseUrl}/users/reset-password`, credentials);
  }

  /**
   * Solicita la renovación del Access Token utilizando la sesión activa del usuario.
   * * Este método es fundamental para el flujo de interceptores. Al recibir un nuevo token,
   * lo guarda automáticamente en el almacenamiento local para sincronizar el estado.
   * * @returns Un `Observable` que emite una cadena de texto (`string`) correspondiente al nuevo token.
   * @throws Propaga el error de red si el proceso de refresco falla (ej. sesión expirada).
   */
  refreshToken() {
    return this.http
      .post<{ success: boolean; token: string }>(
        `${this.baseUrl}/users/refresh-token`, // 👈 Apunta a tu backend real (puerto 3000)
        {},
        { withCredentials: true },
      )
      .pipe(
        tap((response) => {
          if (response.success) {
            this.tokenStorage.saveToken(response.token); // Guardamos el nuevo Access Token
          }
        }),
        map((response) => response.token),
      );
  }

  /**
   * Finaliza la sesión del usuario limpiando los rastros de autenticación.
   * * Remueve los tokens del almacenamiento local y redirige forzosamente a la página de login
   * para asegurar que el estado de la aplicación sea consistente con el cierre de sesión.
   */
  logout() {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    this.router.navigate(['/login']);
  }
}
